<template>
    <div>
        <div class="d-inline-flex flex-wrap mb-4 rounded-lg mobile-header-border justify-space-between align-center">
            <v-col cols="2" lg="auto">
                <v-card elevation="0" class="pa-4 rounded-lg">
                    <div class="d-flex flex-column flex-lg-row align-center align-lg-center text-center text-lg-left">
                        <div class="rounded-lg pa-3 mb-2 mb-sm-0 mr-lg-2 plusIndigoLighten5">
                            <v-icon size="36" color="plusIndigoDarken4">mdi-account-group</v-icon>
                        </div>
                        <div>
                            <div class="font-roboto text-h5">
                                <v-progress-circular size="20" v-if="loading" :width="2" indeterminate></v-progress-circular>
                                <span v-else>{{ pending.length }}</span>
                            </div>
                            <div class="text-body-2 font-roboto">{{ $t("message.total") }}</div>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </div>

        <!-- Filter card -->
        <v-card elevation="0" class="mb-lg-3 mt-1 pa-6 rounded-lg border-filter">
            <v-card-text class="pa-0">
                <v-row>
                    <v-col class="pb-0" cols="12" lg="4">
                        <v-text-field :hide-details="true" dense outlined
                            :label="$t('message.search_by_name_filter')"
                            v-model="filterTableValue"
                            prepend-inner-icon="mdi-magnify">
                        </v-text-field>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Desktop View -->
        <v-card v-if="!isSmallerDisplay()">
            <v-data-table outlined v-model="selectedItems" item-key="_id" :headers="headersPending" :items="filteredPending"
                class="elevation-1 font-roboto" :loading="loading" :loading-text="$t('message.loading')"
                :no-data-text="$t('message.no_data')" :footer-props="{
                    itemsPerPageText: $t('message.rows_per_page'),
                    itemsPerPageOptions: [25, 50, 75],
                }">

                <template v-slot:item.avatar="{ item }">
                    <div class="d-flex align-center text-xs-center clickable"
                        @click="goToMember(item._id, item.displayName)">
                        <v-avatar size="36" v-if="item.userImageSmall || item.userImage">
                            <ImageDisplay :src="item.userImageSmall ? item.userImageSmall : item.userImage" />
                        </v-avatar>
                        <v-avatar size="36" color="plusIndigoLighten3" v-else>
                            <span class="font-roboto white--text text-subtitle-1">{{ getInitials(item.displayName)
                                }}</span>
                        </v-avatar>
                        <country-display class="ml-3" :country-code="item.nationality" />
                    </div>
                </template>

                <template v-slot:item.displayName="{ item }">
                    <span class="mr-1">{{ item.displayName }}</span>
                </template>

                <template v-slot:item.belt="{ item }">
                    <belt-display :belt="item.belt" :show-text="false" />
                </template>

                <template v-slot:item.actions="{ item }">
                    <v-btn icon v-allowed="'approve_member'" @click="approveMember(item, false)">
                        <v-icon>mdi-plus-circle-outline</v-icon>
                    </v-btn>
                    <v-btn icon v-allowed="'approve_member'" @click="rejectMember(item, false)">
                        <v-icon>mdi-minus-circle-outline</v-icon>
                    </v-btn>
                </template>
            </v-data-table>
        </v-card>

        <!-- Mobile View -->
        <v-card v-else>
            <v-skeleton-loader v-if="loading"
                type="list-item-avatar, list-item-avatar, list-item-avatar" />
            <v-list v-else-if="filteredPending && filteredPending.length > 0">
                <template v-for="(m, index) in filteredPending">
                    <v-list-item :key="'pending_member_' + m._id">
                        <v-list-item-avatar>
                            <member-avatar :status="m.lastPaymentStatus"
                                :image="m.userImageSmall ? m.userImageSmall : m.userImage" size="40"></member-avatar>
                        </v-list-item-avatar>
                        <v-list-item-content class="text-left">
                            <v-list-item-title>{{ m.displayName }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-menu offset-y>
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" fab text small slot="activator" left>
                                        <v-icon>mdi-dots-vertical</v-icon>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item>
                                        <v-list-item-title>
                                            <v-icon v-allowed="'approve_member'" title="Approve"
                                                @click="approveMember(m, false)">mdi-plus-circle-outline</v-icon>
                                        </v-list-item-title>
                                    </v-list-item>
                                    <v-list-item>
                                        <v-list-item-title>
                                            <v-icon title="Reject" @click="rejectMember(m, false)"
                                                v-allowed="'reject_member'">mdi-minus-circle-outline</v-icon>
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </v-list-item-action>
                    </v-list-item>
                    <v-divider v-if="index + 1 < pending.length" :key="'pending_member_index_' + index"></v-divider>
                </template>
            </v-list>
        </v-card>
    </div>
</template>

<script>
import BeltDisplay from "@/components/displays/BeltDisplay.vue";
import CountryDisplay from "@/components/displays/CountryFlagDisplay.vue";
import MemberAvatar from "@/components/displays/MemberAvatar.vue";
import Api from "@/services/Api";
import Api2 from "@/services/Api2";

export default {
    name: "ViewPendingMembers",
    components: {
        CountryDisplay,
        BeltDisplay,
        MemberAvatar,
    },
    props: {
        academy: Object,
        academyUser: Object,
        user: Object
    },
    data() {
        return {
            pending: [],
            filteredPending: [],
            filterTableValue: '',
            selectedItems: [],
            loading: false,
            headersPending: [
                {
                    text: "",
                    align: "left",
                    sortable: false,
                    value: "avatar",
                },
                { text: this.$t("message.name"), value: "displayName" },
                { text: this.$t("message.belt"), value: "belt" },
                {
                    text: "",
                    align: "left",
                    sortable: false,
                    value: "actions",
                }
            ],
        };
    },
    created() {
        this.getPendingMembers();
    },
    watch: {
        filterTableValue(val) {
            this.filterTable(val);
        },
        pending() {
            this.filterTable(this.filterTableValue);
        },
    },
    methods: {
        async getPendingMembers() {
            this.loading = true;
            try {
                const response = await Api2().get(
                    "admins/academy/" +
                    this.$store.state.selectedAcademy +
                    "/members?get_subscriptions=true&get_curriculum_instances=true"
                );

                const data = response.data;
                if (data && data.pending) {
                    this.pending = data.pending;
                }

                this.loading = false;
                this.$emit('pending-members-loaded', data);
            } catch (e) {
                this.loading = false;
                this.showAlert("error", "Oops!", this.$t("message.error_generic"));
            }
        },

        async approveMember(member, reapprove) {
            let data = { members: [member._id] };
            if (reapprove) data["rejected"] = true;

            try {
                const response = await Api().post(
                    "admins/academy/" + this.$store.state.selectedAcademy + "/approve",
                    data
                );

                if (response.data) {
                    // Remove from pending list
                    const index = this.pending.findIndex(p => p._id === member._id);
                    if (index > -1) {
                        this.pending.splice(index, 1);
                    }

                    this.showAlert("success", this.$t("message.member_approved"));
                    this.$emit('member-approved', member);
                    this.$emit('refresh-search');
                }
            } catch (e) {
                this.showAlert("error", "Oops!", this.$t("message.error_generic"));
            }
        },

        // eslint-disable-next-line no-unused-vars
        async rejectMember(member, approved) {
            try {
                const response = await Api().post(
                    "admins/academy/" + this.$store.state.selectedAcademy + "/reject",
                    { members: [member._id] }
                );

                if (response.data) {
                    // Remove from pending list
                    const index = this.pending.findIndex(p => p._id === member._id);
                    if (index > -1) {
                        this.pending.splice(index, 1);
                    }

                    this.showAlert("success", this.$t("message.member_rejected"));
                    this.$emit('member-rejected', member);
                }
            } catch (e) {
                if (e.response && e.response.data && e.response.data.message_translation) {
                    this.showAlert(
                        "error",
                        this.$t("message." + e.response.data.message_translation),
                        null,
                        8000
                    );
                } else {
                    this.showAlert("error", "Oops!", this.$t("message.error_generic"));
                }
            }
        },

        filterTable(value = '') {
            if (!value) {
                this.filteredPending = this.pending;
                return;
            }
            const v = value.toLowerCase();
            this.filteredPending = this.pending.filter(m => {
                const name = (m.displayName || '').toLowerCase();
                const email = (m.email || '').toLowerCase();
                return name.indexOf(v) >= 0 || email.indexOf(v) >= 0;
            });
        },

        goToMember(id, displayName) {
            this.navigateTo({
                name: "viewmember",
                params: { memberid: id },
                crumbName: displayName
            });
        },

        // Utility methods
        isSmallerDisplay() {
            return this.$vuetify.breakpoint.smAndDown;
        }
    }
};
</script>

<style scoped>
.clickable {
    cursor: pointer;
}

.border-filter {
    border: 1px solid var(--v-plusBorder-base) !important;
    background-color: var(--v-plusBackground-base) !important;
}
</style>